import type { L10n } from '../types'
import type { BeeArchetypeId, RecapModule } from '../types/engagement'

/**
 * RECAP CONFIGURATION — single prototype source (Client Review — Revision 03).
 *
 * Read by the Mobile recap (utils/recap.ts, components/recap/*) AND by
 * Admin → Gamificação → Retrospectiva. Admin edits update the in-memory copy
 * held in hooks/recapConfigStore.ts, so the Mobile recap reflects them at once.
 * Changes reset on reload — versioned persistence is Marco 3.
 *
 * Everything here is CONFIGURABLE content/parameters. Fixed product and privacy
 * rules (Daily Check-only Bee type, no health data, one reward per recap, bonus Bee
 * excluded from the Post unlock…) live in code and are never editable.
 */

export interface BeeArchetypeCopy {
  name: L10n
  description: L10n
}

export interface RecapConfig {
  /**
   * "Seu tipo de abelha" catalogue. Names and descriptions are PROTOTYPE COPY and may be
   * refined by the client without changing the selection logic (Daily Check consistency only).
   */
  archetypes: Record<BeeArchetypeId, BeeArchetypeCopy>
  /** Bonus Bee for the first CONFIRMED share of a recap. Sample value. */
  shareRewardBee: number
  /** Minimum eligible members before a community percentile is shown. Privacy parameter. */
  minCommunityPopulation: number
  /** Minimum likes + comments before "Quem esteve com você" appears. */
  minSocialInteractions: number
  /** Consistency gap (percentage points) under which top habits count as tied → balanced Bee. */
  archetypeTieTolerancePct: number
  /** Max spread (percentage points) between strongest and weakest habit that counts as balanced. */
  archetypeBalancedSpreadPct: number
  /** Recap modules that can be turned on/off. */
  modules: Record<RecapModule, boolean>
}

export const recapConfigDefaults: RecapConfig = {
  archetypes: {
    movement: {
      name: { 'pt-BR': 'Abelha do Movimento', 'en-US': 'Movement Bee' },
      description: {
        'pt-BR': 'Você manteve o corpo em movimento com constância. Cada treino somou.',
        'en-US': 'You kept your body moving, steadily. Every session added up.',
      },
    },
    hydration: {
      name: { 'pt-BR': 'Abelha da Hidratação', 'en-US': 'Hydration Bee' },
      description: {
        'pt-BR': 'A água foi sua aliada mais fiel. Um cuidado simples, feito com constância.',
        'en-US': 'Water was your most faithful ally. A simple habit, kept with care.',
      },
    },
    mind: {
      name: { 'pt-BR': 'Abelha Serena', 'en-US': 'Serene Bee' },
      description: {
        'pt-BR': 'Você reservou momentos seus com frequência. Calma também se constrói.',
        'en-US': 'You made time for yourself often. Calm is something you build.',
      },
    },
    nutrition: {
      name: { 'pt-BR': 'Abelha da Nutrição', 'en-US': 'Nutrition Bee' },
      description: {
        'pt-BR': 'Suas escolhas à mesa foram consistentes. Cuidado que vem de dentro.',
        'en-US': 'Your choices at the table were consistent. Care that starts inside.',
      },
    },
    sleep: {
      name: { 'pt-BR': 'Abelha do Descanso', 'en-US': 'Rest Bee' },
      description: {
        'pt-BR': 'Você priorizou boas noites com regularidade. Descansar também é evoluir.',
        'en-US': 'You prioritised good nights regularly. Rest is progress too.',
      },
    },
    balanced: {
      name: { 'pt-BR': 'Abelha do Equilíbrio', 'en-US': 'Balance Bee' },
      description: {
        'pt-BR': 'Seus hábitos caminharam juntos, sem um único destaque. Equilíbrio é uma conquista.',
        'en-US': 'Your habits moved together, with no single standout. Balance is an achievement.',
      },
    },
  },
  shareRewardBee: 10,
  minCommunityPopulation: 50,
  minSocialInteractions: 3,
  archetypeTieTolerancePct: 3,
  archetypeBalancedSpreadPct: 8,
  modules: {
    beeType: true,
    hive: true,
    consistency: true,
    habit: true,
    activity: true,
    community: true,
    social: true,
    percentile: true,
    milestones: true,
  },
}

/**
 * PROTOTYPE-ONLY. Shows "Simular compartilhamento concluído" on the recap share card so the
 * reward can be demonstrated. Must be false in production — the real trigger is a confirmed
 * share event defined in Marco 3.
 */
export const PROTOTYPE_SHARE_SIMULATION = true
